import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Users, Calendar, MapPin, HandHelping, Megaphone, FileText, ChevronRight, ExternalLink, CheckCircle } from 'lucide-react';

const GetInvolved = () => {
  // Color palette from the existing app
  const colorPalette = {
    primary: '#922c40',   // Burgundy/wine red
    secondary: '#DC9750', // Bronze/gold
    tertiary: '#F3EAC0',  // Cream/beige
    light: '#F9F5E3',     // Lighter cream for backgrounds
    dark: '#1E2640',      // Navy/dark blue
    white: '#FFFFFF',
    black: '#333333'
  };

  // State for form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    zip: '',
    interests: [],
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  
  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prevState => {
      if (checked) {
        return { ...prevState, interests: [...prevState.interests, value] };
      } else {
        return { ...prevState, interests: prevState.interests.filter(interest => interest !== value) };
      }
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, you would send this data to your server or API
    console.log('Form submitted:', formData);
    setSubmitted(true);
    
    // Reset form after a delay (for demo purposes)
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        zip: '',
        interests: [],
        message: ''
      });
      setSubmitted(false);
    }, 5000);
  };

  return (
    <div style={{ backgroundColor: colorPalette.light }}>
      {/* Hero Section */}
      <section style={{
        backgroundColor: colorPalette.dark,
        color: 'white',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <h1 style={{ 
            fontSize: '3rem',
            marginBottom: '16px',
            fontWeight: '800'
          }}>Join the Movement</h1>
          <p style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 24px',
            lineHeight: '1.6',
            opacity: '0.9'
          }}>
            Help us transform American democracy through proportional representation. Anyone who is tired of the two-party system and wants to fix it is welcome to join.
          </p>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: 'white'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <h2 style={{
            fontSize: '2.2rem',
            color: colorPalette.dark,
            marginBottom: '40px',
            textAlign: 'center'
          }}>Take Action Now</h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 
              window.innerWidth < 768 ? '1fr' : 
              window.innerWidth < 992 ? 'repeat(2, 1fr)' : 
              'repeat(3, 1fr)',
            gap: '30px',
            marginBottom: '40px'
          }}>
            {/* Quick Action Card 1 */}
            <div style={{
              backgroundColor: colorPalette.light,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              ':hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <div style={{
                backgroundColor: colorPalette.primary,
                padding: '20px',
                textAlign: 'center'
              }}>
                <Mail size={40} color="white" />
              </div>
              <div style={{ padding: '24px' }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  marginBottom: '12px',
                  color: colorPalette.dark
                }}>Sign Up for Updates</h3>
                <p style={{ marginBottom: '20px' }}>
                  Join our mailing list to receive campaign news, action alerts, and educational resources.
                </p>
                <a href="#join-form" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: colorPalette.primary,
                  fontWeight: '600',
                  textDecoration: 'none'
                }}>
                  Sign up now
                  <ChevronRight size={16} style={{ marginLeft: '4px' }} />
                </a>
              </div>
            </div>
            
            {/* Quick Action Card 2 */}
            <div style={{
              backgroundColor: colorPalette.light,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              ':hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <div style={{
                backgroundColor: colorPalette.secondary,
                padding: '20px',
                textAlign: 'center'
              }}>
                <Users size={40} color="white" />
              </div>
              <div style={{ padding: '24px' }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  marginBottom: '12px',
                  color: colorPalette.dark
                }}>Join a Local Group</h3>
                <p style={{ marginBottom: '20px' }}>
                  Connect with others in your area working on electoral reform and proportional representation.
                </p>
                <a href="#local-groups" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: colorPalette.secondary,
                  fontWeight: '600',
                  textDecoration: 'none'
                }}>
                  Find a group
                  <ChevronRight size={16} style={{ marginLeft: '4px' }} />
                </a>
              </div>
            </div>
            
            {/* Quick Action Card 3 */}
            <div style={{
              backgroundColor: colorPalette.light,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              ':hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <div style={{
                backgroundColor: colorPalette.dark,
                padding: '20px',
                textAlign: 'center'
              }}>
                <Calendar size={40} color="white" />
              </div>
              <div style={{ padding: '24px' }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  marginBottom: '12px',
                  color: colorPalette.dark
                }}>Attend an Event</h3>
                <p style={{ marginBottom: '20px' }}>
                  Participate in virtual and in-person events to learn more and connect with other advocates.
                </p>
                <a href="#upcoming-events" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: colorPalette.dark,
                  fontWeight: '600',
                  textDecoration: 'none'
                }}>
                  View calendar
                  <ChevronRight size={16} style={{ marginLeft: '4px' }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Get Involved Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: colorPalette.light
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <h2 style={{
            fontSize: '2.2rem',
            color: colorPalette.dark,
            marginBottom: '40px',
            textAlign: 'center'
          }}>Ways to Get Involved</h2>
          
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              marginBottom: '30px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.primary}20`,
                  color: colorPalette.primary,
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '20px',
                  flexShrink: 0
                }}>
                  <Megaphone size={24} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '1.6rem', 
                    marginBottom: '12px', 
                    color: colorPalette.dark 
                  }}>Advocate & Educate</h3>
                  <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>Share information about proportional representation on social media</li>
                    <li style={{ marginBottom: '8px' }}>Have conversations with friends, family, and neighbors about electoral reform</li>
                    <li style={{ marginBottom: '8px' }}>Host a house party or educational event in your community</li>
                    <li style={{ marginBottom: '8px' }}>Write letters to the editor or op-eds for local publications</li>
                    <li>Contact your elected officials about proportional representation reforms</li>
                  </ul>
                  <div style={{
                    backgroundColor: `${colorPalette.primary}10`,
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '20px'
                  }}>
                    <h4 style={{ 
                      fontSize: '1.1rem', 
                      marginBottom: '8px',
                      color: colorPalette.primary
                    }}>Helpful Resources:</h4>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      <li style={{ marginBottom: '8px' }}>
                        <Link to="/resources" style={{ color: colorPalette.primary, fontWeight: '500' }}>Educational Materials</Link> - Handouts, graphics, and talking points
                      </li>
                      <li>
                        <Link to="/voting-systems" style={{ color: colorPalette.primary, fontWeight: '500' }}>Voting Systems Guide</Link> - Learn about different electoral systems
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              marginBottom: '30px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.secondary}20`,
                  color: colorPalette.secondary,
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '20px',
                  flexShrink: 0
                }}>
                  <HandHelping size={24} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '1.6rem', 
                    marginBottom: '12px', 
                    color: colorPalette.dark 
                  }}>Volunteer Locally</h3>
                  <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>Join or start a local group in your community</li>
                    <li style={{ marginBottom: '8px' }}>Collect signatures for ballot initiatives supporting electoral reform</li>
                    <li style={{ marginBottom: '8px' }}>Phonebank or canvass for electoral reform campaigns</li>
                    <li style={{ marginBottom: '8px' }}>Organize or participate in coalition-building meetings with partner organizations</li>
                    <li>Provide skills-based support (graphic design, web development, legal research, etc.)</li>
                  </ul>
                  <div style={{
                    backgroundColor: `${colorPalette.secondary}10`,
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '20px'
                  }}>
                    <p style={{ margin: 0, fontWeight: '500', fontSize: '0.95rem' }}>
                      Ready to volunteer? <a href="#volunteer-form" style={{ color: colorPalette.secondary, fontWeight: '600' }}>Fill out our volunteer form</a> and we'll connect you with opportunities in your area.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              marginBottom: '30px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.dark}20`,
                  color: colorPalette.dark,
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '20px',
                  flexShrink: 0
                }}>
                  <FileText size={24} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '1.6rem', 
                    marginBottom: '12px', 
                    color: colorPalette.dark 
                  }}>Contribute & Support</h3>
                  <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>Make a donation to support our educational and organizing work</li>
                    <li style={{ marginBottom: '8px' }}>Contribute to our mutual aid programs that build community power</li>
                    <li style={{ marginBottom: '8px' }}>Share your professional skills or expertise with our campaign</li>
                    <li style={{ marginBottom: '8px' }}>Host a fundraiser with your network to support the movement</li>
                    <li>Provide in-kind donations of materials, services, or meeting spaces</li>
                  </ul>
                  <div style={{
                    backgroundColor: `${colorPalette.dark}10`,
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '20px',
                    textAlign: 'center'
                  }}>
                    <a href="#donate" style={{
                      display: 'inline-block',
                      backgroundColor: colorPalette.dark,
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      fontSize: '1.1rem'
                    }}>
                      Donate Now
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Form Section */}
      <section id="join-form" style={{
        padding: '80px 0',
        backgroundColor: 'white'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontSize: '2.2rem',
                color: colorPalette.dark,
                marginBottom: '16px'
              }}>Join the Movement</h2>
              <p style={{ marginBottom: '20px', fontSize: '1.1rem' }}>
                Sign up to receive updates, action alerts, and information about how you can help transform American democracy through proportional representation.
              </p>
              <ul style={{
                paddingLeft: '20px',
                marginBottom: '20px',
                fontSize: '1rem'
              }}>
                <li style={{ marginBottom: '8px' }}>Receive our monthly newsletter</li>
                <li style={{ marginBottom: '8px' }}>Get alerts about local events and actions</li>
                <li style={{ marginBottom: '8px' }}>Access educational resources and materials</li>
                <li>Connect with advocates in your area</li>
              </ul>
              <div style={{
                backgroundColor: colorPalette.tertiary,
                padding: '15px',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}>
                We respect your privacy and will never sell or share your information with third parties. You can unsubscribe at any time.
              </div>
            </div>
            
            <div style={{
              backgroundColor: colorPalette.light,
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              {submitted ? (
                <div style={{
                  textAlign: 'center',
                  padding: '30px 0'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: `${colorPalette.primary}20`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <CheckCircle size={30} color={colorPalette.primary} />
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    marginBottom: '12px',
                    color: colorPalette.dark
                  }}>Thank You!</h3>
                  <p style={{ marginBottom: '0' }}>
                    You've successfully joined the Voters Deserve More movement. Check your email for a confirmation message and next steps.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h3 style={{
                    fontSize: '1.5rem',
                    marginBottom: '20px',
                    color: colorPalette.dark,
                    textAlign: 'center'
                  }}>
                    Sign Up
                  </h3>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      Full Name*
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      Email Address*
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      ZIP Code*
                    </label>
                    <input
                      type="text"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{5}"
                      title="Five digit ZIP code"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '12px',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      I'm interested in: (select all that apply)
                    </label>
                    
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          name="interests"
                          value="volunteering"
                          checked={formData.interests.includes('volunteering')}
                          onChange={handleCheckboxChange}
                          style={{ marginRight: '8px' }}
                        />
                        Volunteering locally
                      </label>
                    </div>
                    
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          name="interests"
                          value="donating"
                          checked={formData.interests.includes('donating')}
                          onChange={handleCheckboxChange}
                          style={{ marginRight: '8px' }}
                        />
                        Making a donation
                      </label>
                    </div>
                    
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          name="interests"
                          value="learning"
                          checked={formData.interests.includes('learning')}
                          onChange={handleCheckboxChange}
                          style={{ marginRight: '8px' }}
                        />
                        Learning more about electoral reform
                      </label>
                    </div>
                  </div>
                  
                  <button type="submit" style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: colorPalette.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}>
                    Join Now
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section style={{
        padding: '80px 0',
        backgroundColor: colorPalette.primary,
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '900px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <h2 style={{
            fontSize: '2.8rem',
            fontWeight: '800',
            marginBottom: '20px'
          }}>Ready to Transform American Democracy?</h2>
          
          <p style={{
            fontSize: '1.25rem',
            maxWidth: '700px',
            margin: '0 auto 40px',
            lineHeight: '1.6',
            opacity: '0.9'
          }}>
            Together, we can break the two-party system and create a democracy that truly represents all Americans. Join us today!
          </p>
          
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a href="#join-form" style={{
              backgroundColor: 'white',
              color: colorPalette.primary,
              padding: '16px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '1.1rem'
            }}>
              Join the Movement
            </a>
            
            <a href="#donate" style={{
              backgroundColor: 'transparent',
              color: 'white',
              padding: '16px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '1.1rem',
              border: '2px solid white'
            }}>
              Donate
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;